import type { ConnectionState, HistoryItem } from "./types";
import { renderQr } from "./qr";

export interface UICallbacks {
  onCreate: () => void;
  onJoin: (code: string) => void;
  onCancel: () => void;
  onSendText: (body: string) => void;
  onSendFiles: (files: File[]) => void;
}

export interface Notice {
  title: string;
  body: string;
  cta: string;
}

interface UIState {
  state: ConnectionState;
  code: string;
  errorMessage: string;
  history: HistoryItem[];
  notice: Notice | null;
}

const PAPER_AIRPLANE_SVG = `<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M2 21l21-9L2 3v7l15 2-15 2z"/></svg>`;
const CLIPBOARD_SVG = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="9" y="2" width="6" height="4" rx="1"/><path d="M9 4H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2h-2"/></svg>`;
const CHECK_SVG = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>`;
const ARROW_UP_SVG = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 19V5M5 12l7-7 7 7"/></svg>`;

const MAX_BUBBLES_KEY = "webclipboard.maxBubbles.v1";
const DEFAULT_MAX_BUBBLES = 200;
const MIN_MAX_BUBBLES = 1;
const MAX_MAX_BUBBLES = 10_000;

function loadMaxBubbles(): number {
  try {
    const raw = localStorage.getItem(MAX_BUBBLES_KEY);
    if (!raw) return DEFAULT_MAX_BUBBLES;
    const n = Number.parseInt(raw, 10);
    if (!Number.isFinite(n)) return DEFAULT_MAX_BUBBLES;
    return Math.max(MIN_MAX_BUBBLES, Math.min(MAX_MAX_BUBBLES, n));
  } catch {
    return DEFAULT_MAX_BUBBLES;
  }
}

function saveMaxBubbles(n: number): void {
  try {
    localStorage.setItem(MAX_BUBBLES_KEY, String(n));
  } catch {}
}

export class UI {
  private root: HTMLElement;
  private state: UIState = {
    state: "idle",
    code: "",
    errorMessage: "",
    history: [],
    notice: null,
  };
  private toastTimer?: number;
  private toastEl: HTMLDivElement | null = null;
  private blobUrls = new Map<string, string>();
  private lastCopiedText: string | null = null;
  private dropOverlay: HTMLDivElement | null = null;
  private dragDepth = 0;
  private maxBubbles: number = loadMaxBubbles();
  private backToTop: HTMLButtonElement | null = null;

  constructor(root: HTMLElement, private cb: UICallbacks) {
    this.root = root;
    this.installDropOverlay();
    this.installBackToTop();
    this.installToast();
    this.render();
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") {
        void this.refreshFromClipboard();
      }
    });
    document.addEventListener("copy", () => {
      // Native cmd/ctrl+C — clipboard is about to update; recheck shortly.
      window.setTimeout(() => void this.refreshFromClipboard(), 50);
    });
    window.addEventListener("scroll", () => this.updateBackToTop(), {
      passive: true,
    });
    window.addEventListener("resize", () => this.updateBackToTop());
  }

  private installBackToTop(): void {
    const btn = document.createElement("button");
    btn.className = "back-to-top";
    btn.setAttribute("aria-label", "back to top");
    btn.innerHTML = ARROW_UP_SVG;
    btn.addEventListener("click", () => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    });
    document.body.appendChild(btn);
    this.backToTop = btn;
  }

  private updateBackToTop(): void {
    if (!this.backToTop) return;
    const sendPane = this.root.querySelector<HTMLElement>(".send-pane");
    if (!sendPane) {
      this.backToTop.classList.remove("show");
      return;
    }
    const rect = sendPane.getBoundingClientRect();
    if (rect.bottom < 0) this.backToTop.classList.add("show");
    else this.backToTop.classList.remove("show");
  }

  setMaxBubbles(n: number): void {
    const clamped = Math.max(
      MIN_MAX_BUBBLES,
      Math.min(MAX_MAX_BUBBLES, Math.floor(n))
    );
    this.maxBubbles = clamped;
    saveMaxBubbles(clamped);
    this.trimHistory();
  }

  getMaxBubbles(): number {
    return this.maxBubbles;
  }

  private trimHistory(): void {
    if (this.state.history.length <= this.maxBubbles) return;
    const drop = this.state.history.length - this.maxBubbles;
    // Drop oldest entries (kept at the end of state.history).
    this.state.history = this.state.history.slice(drop);
    // Activity is rendered latest-first, so the oldest bubbles are the
    // last children of the .history list — remove that many from the bottom.
    const list = this.root.querySelector<HTMLElement>(".history");
    if (!list) return;
    for (let i = 0; i < drop; i++) {
      const last = list.lastElementChild;
      if (!last) break;
      const id = (last as HTMLElement).dataset.itemId;
      if (id) {
        const url = this.blobUrls.get(id);
        if (url) {
          URL.revokeObjectURL(url);
          this.blobUrls.delete(id);
        }
      }
      last.remove();
    }
  }

  private installDropOverlay(): void {
    const overlay = document.createElement("div");
    overlay.className = "drop-overlay";
    const text = document.createElement("div");
    text.className = "drop-overlay-text";
    text.textContent = "Drop to toss";
    overlay.append(text);
    document.body.appendChild(overlay);
    this.dropOverlay = overlay;

    document.addEventListener("dragenter", (e) => {
      const types = e.dataTransfer?.types;
      if (!types || !Array.from(types).includes("Files")) return;
      this.dragDepth += 1;
      if (this.isAcceptingFiles()) overlay.classList.add("show");
    });
    document.addEventListener("dragleave", () => {
      this.dragDepth = Math.max(0, this.dragDepth - 1);
      if (this.dragDepth === 0) overlay.classList.remove("show");
    });
    document.addEventListener("dragover", (e) => {
      const types = e.dataTransfer?.types;
      if (types && Array.from(types).includes("Files")) e.preventDefault();
    });
    document.addEventListener("drop", (e) => {
      e.preventDefault();
      this.dragDepth = 0;
      overlay.classList.remove("show");
      const files = Array.from(e.dataTransfer?.files ?? []);
      if (files.length === 0) return;
      if (!this.isAcceptingFiles()) {
        this.showToast("pair first to toss files");
        return;
      }
      this.cb.onSendFiles(files);
    });
  }

  private isAcceptingFiles(): boolean {
    return this.state.state === "connected";
  }

  private async refreshFromClipboard(): Promise<void> {
    if (this.lastCopiedText === null) return;
    if (!navigator.clipboard?.readText) return;
    try {
      const txt = await navigator.clipboard.readText();
      if (txt !== this.lastCopiedText) {
        this.lastCopiedText = null;
        this.refreshCopyButtons();
      }
    } catch {
      // Permission denied or unsupported — leave state as-is.
    }
  }

  private refreshCopyButtons(): void {
    const bubbles = this.root.querySelectorAll<HTMLElement>(".bubble[data-item-id]");
    bubbles.forEach((b) => {
      const id = b.dataset.itemId;
      if (!id) return;
      const item = this.state.history.find((x) => x.id === id);
      if (!item || item.kind !== "text") return;
      const btn = b.querySelector<HTMLButtonElement>(".bubble-header .btn");
      if (!btn) return;
      this.fillCopyButton(btn, item.body);
    });
  }

  private fillCopyButton(btn: HTMLButtonElement, body: string): void {
    const copied = this.lastCopiedText !== null && body === this.lastCopiedText;
    const icon = copied ? CHECK_SVG : CLIPBOARD_SVG;
    const label = copied ? "copied" : "copy";
    btn.innerHTML = `${icon}<span>${label}</span>`;
  }

  setState(s: ConnectionState, errorMessage = ""): void {
    this.state.state = s;
    this.state.errorMessage = errorMessage;
    this.render();
  }

  setCode(code: string): void {
    this.state.code = code;
    this.render();
  }

  appendItem(item: HistoryItem): void {
    const isFirst = this.state.history.length === 0;
    this.state.history = [...this.state.history, item];
    const list = this.root.querySelector<HTMLElement>(".history");
    if (!list || isFirst) {
      // No activity card yet (or first item — renderSession only shows it
      // when history.length > 0). Full render once to introduce the card.
      this.render();
      this.trimHistory();
      return;
    }
    list.prepend(this.renderBubble(item));
    this.trimHistory();
  }

  patchItem(id: string, patch: Partial<HistoryItem>): void {
    this.state.history = this.state.history.map((it) =>
      it.id === id ? ({ ...it, ...patch } as HistoryItem) : it
    );
    const updated = this.state.history.find((it) => it.id === id);
    if (!updated) return;
    const existing = this.root.querySelector<HTMLElement>(
      `[data-item-id="${id}"]`
    );
    if (!existing) return; // not currently rendered, state is enough
    this.updateBubble(existing, updated);
  }

  private updateBubble(node: HTMLElement, item: HistoryItem): void {
    if (item.kind !== "file") return; // text bubbles don't change after creation
    const sizeEl = node.querySelector<HTMLElement>(".filesize");
    if (sizeEl) {
      sizeEl.textContent = `${formatBytes(item.bytes)} / ${formatBytes(item.size)}`;
    }
    const fill = node.querySelector<HTMLElement>(".progress > div");
    if (fill) {
      const pct = item.size > 0 ? Math.min(100, (item.bytes / item.size) * 100) : 0;
      fill.style.width = `${pct}%`;
    }
    if (item.blob && item.bytes >= item.size && !node.querySelector("a.btn")) {
      let url = this.blobUrls.get(item.id);
      if (!url) {
        url = URL.createObjectURL(item.blob);
        this.blobUrls.set(item.id, url);
      }
      const a = document.createElement("a");
      a.href = url;
      a.download = item.name;
      a.textContent = "download";
      a.className = "btn small";
      a.style.marginTop = "4px";
      a.style.alignSelf = "flex-start";
      node.append(a);
    }
  }

  getHistory(): HistoryItem[] {
    return this.state.history;
  }

  setHistory(items: HistoryItem[]): void {
    for (const url of this.blobUrls.values()) URL.revokeObjectURL(url);
    this.blobUrls.clear();
    // Cap on restore — if the saved history exceeds the cap, keep newest.
    this.state.history =
      items.length > this.maxBubbles ? items.slice(-this.maxBubbles) : items;
    this.render();
  }

  showToast(text: string, ms = 1800): void {
    if (!this.toastEl) return;
    this.toastEl.textContent = text;
    this.toastEl.classList.add("show");
    clearTimeout(this.toastTimer);
    this.toastTimer = window.setTimeout(() => {
      this.toastEl?.classList.remove("show");
    }, ms);
  }

  private installToast(): void {
    const t = document.createElement("div");
    t.className = "toast";
    document.body.appendChild(t);
    this.toastEl = t;
  }

  showNotice(notice: Notice | null): void {
    this.state.notice = notice;
    this.render();
  }

  triggerSendAnimation(): void {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const pane = this.root.querySelector(".send-pane");
    if (!pane) return;
    const fly = document.createElement("span");
    fly.className = "airplane-fly";
    fly.innerHTML = PAPER_AIRPLANE_SVG;
    pane.appendChild(fly);
    fly.addEventListener("animationend", () => fly.remove(), { once: true });
  }

  reset(): void {
    for (const url of this.blobUrls.values()) URL.revokeObjectURL(url);
    this.blobUrls.clear();
    this.state = {
      state: "idle",
      code: "",
      errorMessage: "",
      history: [],
      notice: null,
    };
    this.toastEl?.classList.remove("show");
    this.render();
  }

  private render(): void {
    this.root.innerHTML = "";
    this.root.append(this.renderHeader());

    const inSession = isSessionState(this.state.state);
    if (inSession) {
      this.root.append(this.renderSession());
    } else {
      this.root.append(this.renderLanding());
    }

    this.updateBackToTop();
  }

  private renderHeader(): HTMLElement {
    const header = document.createElement("header");
    header.className = "app-header";

    const brand = document.createElement("a");
    brand.className = "brand";
    brand.href = "/";
    brand.innerHTML = `<span class="mark">${PAPER_AIRPLANE_SVG}</span><span class="word">Tosstoss</span>`;
    header.append(brand);

    const meta = document.createElement("nav");
    meta.className = "meta";

    const setting = document.createElement("label");
    setting.className = "setting-keep";
    setting.title = "Maximum messages kept in this session";
    const settingLabel = document.createElement("span");
    settingLabel.textContent = "keep last";
    const settingInput = document.createElement("input");
    settingInput.type = "number";
    settingInput.className = "setting-input";
    settingInput.min = String(MIN_MAX_BUBBLES);
    settingInput.max = String(MAX_MAX_BUBBLES);
    settingInput.step = "1";
    settingInput.value = String(this.maxBubbles);
    settingInput.addEventListener("change", () => {
      const n = Number.parseInt(settingInput.value, 10);
      if (Number.isFinite(n) && n >= MIN_MAX_BUBBLES) {
        this.setMaxBubbles(n);
        settingInput.value = String(this.maxBubbles);
      } else {
        settingInput.value = String(this.maxBubbles);
      }
    });
    setting.append(settingLabel, settingInput);
    meta.append(setting);

    header.append(meta);
    return header;
  }

  private renderLanding(): HTMLElement {
    const wrap = document.createElement("section");
    wrap.append(this.renderHero());
    if (this.state.notice) wrap.append(this.renderNotice(this.state.notice));
    wrap.append(this.renderLandingGrid());
    wrap.append(this.renderPrivacy());
    return wrap;
  }

  private renderSession(): HTMLElement {
    const wrap = document.createElement("section");
    wrap.append(this.renderEndButtonRow());
    wrap.append(this.renderStatusPill());
    wrap.append(this.renderSendPane());
    if (this.state.history.length > 0) wrap.append(this.renderActivity());
    return wrap;
  }

  private renderEndButtonRow(): HTMLElement {
    const row = document.createElement("div");
    row.style.display = "flex";
    row.style.justifyContent = "flex-end";
    row.style.marginBottom = "8px";
    const end = button("end session ✕", "", () => this.cb.onCancel());
    row.append(end);
    return row;
  }

  private renderStatusPill(): HTMLElement {
    const pill = document.createElement("div");
    const reconnecting = this.state.state !== "connected";
    pill.className = reconnecting ? "status-pill reconnecting" : "status-pill";

    const dot = document.createElement("span");
    dot.className = "dot";
    pill.append(dot);

    const label = document.createElement("span");
    if (this.state.state === "connected") {
      label.append(document.createTextNode("Connected to "));
      const code = document.createElement("span");
      code.className = "code";
      code.textContent = formatCode(this.state.code);
      label.append(code);
    } else {
      label.append(document.createTextNode("Reconnecting to "));
      const code = document.createElement("span");
      code.className = "code";
      code.textContent = formatCode(this.state.code);
      label.append(code);
      label.append(document.createTextNode("…"));
    }
    pill.append(label);

    if (reconnecting) {
      const note = document.createElement("span");
      note.className = "note";
      note.textContent = "they'll be back";
      pill.append(note);
    }

    return pill;
  }

  private renderSendPane(): HTMLElement {
    const reconnecting = this.state.state !== "connected";
    const wrap = document.createElement("div");
    wrap.className = "send-pane";

    const ta = document.createElement("textarea");
    ta.placeholder = reconnecting
      ? "Hold tight — reconnecting…"
      : "Type something or drop a file…";
    ta.disabled = reconnecting;
    ta.readOnly = reconnecting;
    wrap.append(ta);

    const actions = document.createElement("div");
    actions.className = "actions";

    const fileInput = document.createElement("input");
    fileInput.type = "file";
    fileInput.multiple = true;
    fileInput.style.display = "none";
    fileInput.addEventListener("change", () => {
      if (fileInput.files && fileInput.files.length > 0) {
        this.cb.onSendFiles(Array.from(fileInput.files));
        fileInput.value = "";
      }
    });
    wrap.append(fileInput);

    const attachBtn = button("⎘ attach", "", () => fileInput.click());
    attachBtn.disabled = reconnecting;
    actions.append(attachBtn);

    const tossBtn = button("Toss →", "primary", () => {
      const v = ta.value.trim();
      if (!v) return;
      this.cb.onSendText(v);
      this.triggerSendAnimation();
      ta.value = "";
    });
    tossBtn.disabled = reconnecting;
    actions.append(tossBtn);

    wrap.append(actions);

    ta.addEventListener("keydown", (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
        e.preventDefault();
        const v = ta.value.trim();
        if (!v || reconnecting) return;
        this.cb.onSendText(v);
        this.triggerSendAnimation();
        ta.value = "";
      }
    });

    return wrap;
  }

  private renderActivity(): HTMLElement {
    const wrap = document.createElement("div");
    wrap.className = "history";
    // Latest first.
    for (let i = this.state.history.length - 1; i >= 0; i--) {
      wrap.append(this.renderBubble(this.state.history[i]!));
    }
    return wrap;
  }

  private renderBubble(it: HistoryItem): HTMLElement {
    const side = it.direction === "received" ? "caught" : "tossed";
    const bubble = document.createElement("div");
    bubble.className = `bubble ${side}`;
    if (it.kind === "file") bubble.classList.add("file");
    bubble.dataset.itemId = it.id;

    if (it.kind === "text") {
      const header = document.createElement("div");
      header.className = "bubble-header";

      const meta = document.createElement("div");
      meta.className = "meta";
      meta.textContent = `${side} · ${formatRelative(it.at)}`;
      header.append(meta);

      const copy = document.createElement("button");
      copy.className = "btn small";
      copy.setAttribute("aria-label", "copy text");
      this.fillCopyButton(copy, it.body);
      copy.addEventListener("click", async () => {
        try {
          await navigator.clipboard.writeText(it.body);
          this.lastCopiedText = it.body;
          this.refreshCopyButtons();
        } catch {
          this.showToast("couldn't copy");
        }
      });
      header.append(copy);
      bubble.append(header);

      const body = document.createElement("div");
      body.className = "body";
      body.textContent = it.body;
      bubble.append(body);
    } else {
      const meta = document.createElement("div");
      meta.className = "meta";
      meta.textContent = `${side} · ${formatRelative(it.at)}`;
      bubble.append(meta);
      const name = document.createElement("div");
      name.className = "filename";
      name.textContent = it.name;
      bubble.append(name);

      const sub = document.createElement("div");
      sub.className = "filesize";
      sub.textContent = `${formatBytes(it.bytes)} / ${formatBytes(it.size)}`;
      bubble.append(sub);

      const pct = it.size > 0 ? Math.min(100, (it.bytes / it.size) * 100) : 0;
      const prog = document.createElement("div");
      prog.className = "progress";
      const fill = document.createElement("div");
      fill.style.width = `${pct}%`;
      prog.append(fill);
      bubble.append(prog);

      if (it.blob && it.bytes >= it.size) {
        let url = this.blobUrls.get(it.id);
        if (!url) {
          url = URL.createObjectURL(it.blob);
          this.blobUrls.set(it.id, url);
        }
        const a = document.createElement("a");
        a.href = url;
        a.download = it.name;
        a.textContent = "download";
        a.className = "btn small";
        a.style.marginTop = "4px";
        a.style.alignSelf = "flex-start";
        bubble.append(a);
      }
    }

    return bubble;
  }

  private renderHero(): HTMLElement {
    const hero = document.createElement("section");
    hero.className = "hero";
    hero.innerHTML = `
      <h1>Toss text and files between your devices.</h1>
      <p>No account, no upload. Peer-to-peer.</p>
    `;
    return hero;
  }

  private renderLandingGrid(): HTMLElement {
    const grid = document.createElement("div");
    grid.className = "landing-grid";
    grid.append(this.renderHostCard());
    grid.append(this.renderCatcherCard());
    return grid;
  }

  private renderHostCard(): HTMLElement {
    const card = document.createElement("section");
    card.className = "card host-card";

    const h2 = document.createElement("h2");
    h2.textContent = "Toss something";
    card.append(h2);

    const code = document.createElement("div");
    code.className = "code-display";
    code.textContent = this.state.code ? formatCode(this.state.code) : "•••-•••-•••";
    card.append(code);

    const qrRow = document.createElement("div");
    qrRow.className = "qr-row";
    if (this.state.code) {
      const img = document.createElement("img");
      img.alt = "QR for joining";
      img.width = 96;
      img.height = 96;
      renderQr(`${location.origin}/#${this.state.code}`).then((url) => {
        img.src = url;
      });
      qrRow.append(img);

      const copy = button("copy code", "", async () => {
        if (!this.state.code) return;
        await navigator.clipboard.writeText(this.state.code);
        this.showToast("code copied");
      });
      qrRow.append(copy);

      const copyLink = button("copy link", "", async () => {
        if (!this.state.code) return;
        await navigator.clipboard.writeText(`${location.origin}/#${this.state.code}`);
        this.showToast("link copied");
      });
      qrRow.append(copyLink);
    }
    card.append(qrRow);

    const status = document.createElement("p");
    status.className = "status";
    status.style.color = "var(--muted)";
    status.style.fontSize = "13px";
    status.style.margin = "0";
    status.textContent = this.state.code
      ? "● waiting for catcher…"
      : "preparing…";
    card.append(status);

    return card;
  }

  private renderCatcherCard(): HTMLElement {
    const card = document.createElement("section");
    card.className = "card catcher-card";

    const h2 = document.createElement("h2");
    h2.textContent = "Catch a toss";
    card.append(h2);

    const input = document.createElement("input");
    input.className = "code-input";
    input.placeholder = "000-000-000";
    input.maxLength = 11;
    input.inputMode = "numeric";
    input.autocomplete = "off";
    const hint = document.createElement("p");
    hint.style.color = "var(--muted)";
    hint.style.fontSize = "13px";
    hint.style.margin = "10px 0 0";
    hint.textContent = "paste a code or scan QR with another device";

    input.addEventListener("input", () => {
      const digits = input.value.replace(/\D/g, "").slice(0, 9);
      let formatted = digits;
      if (digits.length > 6) {
        formatted = `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
      } else if (digits.length > 3) {
        formatted = `${digits.slice(0, 3)}-${digits.slice(3)}`;
      }
      input.value = formatted;
      if (digits.length === 9) {
        if (digits === this.state.code) {
          hint.textContent = "that's your own code";
          hint.style.color = "var(--accent)";
          return;
        }
        this.cb.onJoin(digits);
      } else {
        hint.textContent = "paste a code or scan QR with another device";
        hint.style.color = "var(--muted)";
      }
    });

    card.append(input);
    card.append(hint);

    setTimeout(() => input.focus(), 0);

    return card;
  }

  private renderPrivacy(): HTMLElement {
    const p = document.createElement("p");
    p.className = "privacy";
    p.textContent =
      "End-to-end encrypted · no one else can see what you send";
    return p;
  }

  private renderNotice(notice: Notice): HTMLElement {
    const card = document.createElement("section");
    card.className = "notice";
    const h2 = document.createElement("h2");
    h2.textContent = notice.title;
    const p = document.createElement("p");
    p.textContent = notice.body;
    const btn = button(notice.cta, "primary", () => {
      this.state.notice = null;
      this.cb.onCreate();
    });
    card.append(h2, p, btn);
    return card;
  }
}

function isSessionState(s: ConnectionState): boolean {
  return (
    s === "connected" ||
    s === "reconnecting" ||
    s === "disconnected" ||
    s === "connecting"
  );
}

export function formatCode(code: string): string {
  if (code.length !== 9) return code;
  return `${code.slice(0, 3)}-${code.slice(3, 6)}-${code.slice(6)}`;
}

export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

export function formatRelative(at: number): string {
  const sec = Math.max(0, Math.round((Date.now() - at) / 1000));
  if (sec < 5) return "just now";
  if (sec < 60) return `${sec}s ago`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  return `${hr}h ago`;
}

function button(
  label: string,
  variant: "primary" | "" = "",
  onClick: () => void
): HTMLButtonElement {
  const b = document.createElement("button");
  b.className = variant ? `btn ${variant}` : "btn";
  b.textContent = label;
  b.addEventListener("click", onClick);
  return b;
}

export { button, PAPER_AIRPLANE_SVG, isSessionState };
